/**
 * useVoiceInput — Lightweight voice-to-text-input hook for non-interview features.
 *
 * Wraps the active adapter selected by the resolved workspace voice profile and provides
 * a simple API for filling text inputs via speech:
 *   - `transcript` — live transcript text (updated as user speaks)
 *   - `start()` / `stop()` — start/stop a voice session
 *   - `status` — current VoiceStatus
 *   - Calls `onTranscriptChange(text)` while dictation is active
 *
 * Used by Talk-to-Data (ChatSidebar), Hilfe-FAB (HelpFab), and Regelwerk-Analyse.
 *
 * DevPlan 0053 — Tasks 9 / 10 / 11
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { useAzureRealtimeAdapter } from './useAzureRealtimeAdapter';
import type { VoiceProvider, VoiceSessionConfig, VoiceStatus } from './useVoiceConversation';

// ── Feature-specific system prompt overrides for voice sessions ────────────────────

export interface UseVoiceInputOptions {
  /** Feature context identifier (e.g. "talk-to-data", "help", "ruleset-analyzer"). */
  featureContext: string;
  /** Workspace ID for scoping. */
  workspaceId: string;
  /** Called with the committed dictation text whenever a speech segment completes. */
  onFinalTranscript?: (text: string) => void;
  /** Called with the full draft text while the user is speaking. */
  onTranscriptChange?: (text: string) => void;
  /** Reads the current input value when dictation starts so speech appends to existing text. */
  getCurrentText?: () => string;
  /** Optional page context (for help feature). */
  pageContext?: string;
  /** Bearer token getter for backend auth. */
  getToken?: () => Promise<string | null>;
}

export interface UseVoiceInputReturn {
  /** Current voice session status. */
  status: VoiceStatus;
  /** Live transcript (updated as user speaks). */
  transcript: string;
  /** Whether microphone is muted. */
  isMuted: boolean;
  /** Whether spoken-answer output is enabled. Non-interview mic input records dictation only. */
  audioOutputEnabled: boolean;
  /** Whether live speech input is supported for the active runtime profile. */
  isSupported: boolean;
  /** Why live speech input is unavailable for the active runtime profile. */
  unsupportedReason: string | null;
  /** Start a voice session. */
  start: () => Promise<void>;
  /** Stop the active voice session. */
  stop: () => void;
  /** Toggle microphone mute. */
  toggleMute: () => void;
  /** Toggle audio output. */
  toggleAudioOutput: () => void;
}

export function useVoiceInput(options: UseVoiceInputOptions): UseVoiceInputReturn {
  const {
    featureContext,
    workspaceId,
    onFinalTranscript,
    onTranscriptChange,
    getCurrentText,
    pageContext,
    getToken,
  } = options;

  const availableProviders = useVoiceStore((s) => s.availableProviders);
  const loading = useVoiceStore((s) => s.loading);
  const audioOutputEnabled = useVoiceStore((s) => s.audioOutputEnabled);
  const setAudioOutputEnabled = useVoiceStore((s) => s.setAudioOutputEnabled);

  const azureAdapter = useAzureRealtimeAdapter();

  const providerAvailability = new Map(availableProviders.map((entry) => [entry.id, entry]));
  const hasAvailabilityMetadata = availableProviders.length > 0;
  const azureAvailable = providerAvailability.get('azure-realtime')?.available ?? false;

  let isSupported = true;
  let unsupportedReason: string | null = null;

  if (loading || !hasAvailabilityMetadata) {
    isSupported = false;
    unsupportedReason = 'Voice-Laufzeit wird geladen.';
  } else {
    if (!azureAvailable) {
      isSupported = false;
      unsupportedReason = 'Azure Realtime ist fuer Spracheingabe aktuell nicht konfiguriert.';
    }
  }

  // Non-interview voice input is dictation-only. Azure Realtime is the STT
  // backend even when the active runtime voice profile is ElevenLabs TTS.
  const adapter: VoiceProvider = azureAdapter;

  const [transcript, setTranscript] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const baseTextRef = useRef('');
  const committedTextRef = useRef('');
  const transcriptRef = useRef('');
  const onFinalRef = useRef(onFinalTranscript);
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const getCurrentTextRef = useRef(getCurrentText);
  onFinalRef.current = onFinalTranscript;
  onTranscriptChangeRef.current = onTranscriptChange;
  getCurrentTextRef.current = getCurrentText;

  const composeDraft = useCallback((liveText = '') => (
    [baseTextRef.current, committedTextRef.current, liveText]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ')
  ), []);

  // Wire transcript + turn-complete events
  useEffect(() => {
    const unsubs = [
      adapter.onTranscript((text: string, isFinal: boolean) => {
        setTranscript(text);
        transcriptRef.current = text;

        if (!isFinal) {
          onTranscriptChangeRef.current?.(composeDraft(text));
          return;
        }

        const finalSegment = text.trim();
        if (finalSegment) {
          committedTextRef.current = [committedTextRef.current, finalSegment]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(' ');

          const finalDraft = composeDraft();
          onTranscriptChangeRef.current?.(finalDraft);
          onFinalRef.current?.(finalDraft);
          setTranscript('');
          transcriptRef.current = '';
        }
      }),
    ];
    return () => {
      unsubs.forEach((unsub) => unsub?.());
    };
  }, [adapter, composeDraft]);

  const start = useCallback(async () => {
    if (!isSupported) {
      return;
    }

    baseTextRef.current = getCurrentTextRef.current?.() ?? '';
    committedTextRef.current = '';
    transcriptRef.current = '';
    setTranscript('');

    const token = getToken ? await getToken() : null;
    const config: VoiceSessionConfig = {
      workspaceId,
      featureContext,
      audioOutput: false,
      voiceId: useVoiceStore.getState().selectedVoice,
      token,
      pageContext,
    };
    await adapter.connect(config);
  }, [adapter, workspaceId, featureContext, pageContext, getToken, isSupported]);

  const stop = useCallback(() => {
    setTranscript('');
    transcriptRef.current = '';
    committedTextRef.current = '';
    baseTextRef.current = '';
    void adapter.disconnect();
  }, [adapter]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
    // TODO: Wire to adapter.muteInput() when available
  }, []);

  const toggleAudioOutput = useCallback(() => {
    const next = !audioOutputEnabled;
    setAudioOutputEnabled(next);
    adapter.setAudioOutput(next);
  }, [adapter, audioOutputEnabled, setAudioOutputEnabled]);

  return {
    status: adapter.status,
    transcript,
    isMuted,
    audioOutputEnabled,
    isSupported,
    unsupportedReason,
    start,
    stop,
    toggleMute,
    toggleAudioOutput,
  };
}
